// Labels come from a small generated lookup (src/data/breadcrumb-labels.json)
// rather than from the content modules themselves. The root route imports this
// file on every page, so importing posts/results-stories/es-pages/es-stories
// here would drag all of that content into the entry bundle.
// Regenerate with: bun scripts/generate-breadcrumb-labels.mjs
import labels from "@/data/breadcrumb-labels.json";


// Build one breadcrumb trail from a pathname. The root route uses the same
// items for the visible navigation and BreadcrumbList structured data so they
// cannot drift apart.

const SITE_URL = "https://virginiabariatric.com";

export interface BreadcrumbItemData {
  name: string;
  path: string;
  url: string;
}

// Human-readable labels for known URL segments.
// Falls back to a title-cased version of the raw segment.
export const SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  team: "Meet Our Team",
  "our-program": "Our Program",
  contact: "Contact",
  schedule: "Schedule a Consultation",
  appointments: "Request an Appointment",

  
  services: "Bariatric Surgery",
  sleeve: "Gastric Sleeve Surgery",

  bypass: "Gastric Bypass",
  "bypass-vs-sleeve": "Gastric Bypass vs. Sleeve",
  band: "Adjustable Gastric Band",
  balloon: "Intragastric Balloon",
  esg: "Endoscopic Sleeve Gastroplasty",
  medication: "Weight Loss Medications",
  "medical-weight-loss": "Medical Weight Loss",
  medications: "Weight Loss Medications: Introduction",
  insurance: "Insurance Coverage",
  "medicaid-cova": "Medicaid & State Employees",
  "medicare-tricare-fehb": "Medicare, TRICARE & FEHB",
  "commercial-marketplace": "Employer & Marketplace Plans",
  "cash-pay-denials": "Denials & Cash Pay",
  nc: "North Carolina",

  revisions: "Revision Surgery",
  tore: "TORe",
  "gastric-bypass": "Gastric Bypass Revision",
  "lap-band": "LAP-BAND Revision",
  results: "Before & After",
  "share-your-story": "Share Your Story",
  resources: "Patient Resources",
  faq: "FAQ",
  forms: "New Patient Forms",
  instructions: "Instructions",
  nutrition: "Nutrition",
  support: "Support Groups",
  prosper: "Financing & Payment Plans",
  suggestion: "Suggestions",
  qualifications: "Do I Qualify?",
  "insurance-coverage": "Insurance Coverage",
  print: "Print",
  "obesity-and-diabetes": "Obesity and Diabetes",
  "managing-weight-regain-after-bariatric-surgery": "Managing Weight Regain",

  semaglutide: "Semaglutide (Wegovy)",
  tirzepatide: "Tirzepatide (Zepbound)",
  "oral-medications": "Oral Medications",
  "app-privacy-policy": "App Privacy Policy",
  "dr-fitzer-dissects-the-news": "Dr. Fitzer Dissects the News",
  sitemap: "Sitemap",
};

function labelFor(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  // Blog posts: use the real post title instead of a title-cased slug.
  const postTitle = (labels.posts as Record<string, string>)[segment];
  if (postTitle) return postTitle;
  // Decode + title-case the raw slug for unmapped segments (e.g., blog post slugs).
  const decoded = (() => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  })();
  return decoded
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Spanish route path -> Spanish nav label, for /es breadcrumbs. */
const ES_LABEL_BY_PATH: Record<string, string> = labels.esPaths;

/**
 * Spanish breadcrumbs: crumb 1 is the Spanish home ("Inicio" -> /es), then the
 * page label in Spanish. Falls back to a title-cased slug for unmapped paths.
 */
function breadcrumbItem(name: string, path: string): BreadcrumbItemData {
  return { name, path, url: `${SITE_URL}${path}` };
}

function buildEsBreadcrumbItems(clean: string): BreadcrumbItemData[] {
  if (clean === "/es") return [];
  const items = [breadcrumbItem("Inicio", "/es")];

  let rest = clean.slice("/es".length).split("/").filter(Boolean);

  // Story pages: /es/antes-y-despues/historia/<slug> has no browsable
  // "/historia" index, so collapse it to Inicio > Antes y después > <paciente>.
  const esStory =
    rest.length === 3 && rest[0] === "antes-y-despues" && rest[1] === "historia"
      ? (labels.esStories as Record<string, string>)[rest[2]!]
      : undefined;
  if (esStory) {
    const hubPath = "/es/antes-y-despues";
    const hubLabel = ES_LABEL_BY_PATH["/es/antes-y-despues"] ?? "Antes y después";
    const leafPath = `/es/antes-y-despues/historia/${rest[2]}`;
    items.push(
      breadcrumbItem(hubLabel, hubPath),
      breadcrumbItem(esStory, leafPath),
    );
    rest = [];
  }

  rest.forEach((seg, idx) => {
    const path = `/es/${rest.slice(0, idx + 1).join("/")}`;
    // "/es/seguro" is a URL prefix, not a real page — point that crumb at the hub.
    const isSeguroPrefix = path === "/es/seguro";
    const itemPath = isSeguroPrefix ? "/es/cobertura-de-seguro" : path;
    const label =
      ES_LABEL_BY_PATH[path] ??
      (isSeguroPrefix
        ? ES_LABEL_BY_PATH["/es/cobertura-de-seguro"] ?? "Cobertura de seguro"
        : seg.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase()));
    items.push(breadcrumbItem(label, itemPath));
  });

  return items;
}

export function buildBreadcrumbItems(pathname: string): BreadcrumbItemData[] {
  if (!pathname || pathname === "/") return [];
  // Strip query/hash and any trailing slash so @id matches the page canonical exactly.
  const clean = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "");
  let segments = clean.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  if (segments[0] === "es") return buildEsBreadcrumbItems(clean);

  // Patient story pages: /results/story/<slug> has no browsable "/results/story"
  // index, so collapse it to Home > Before & After > <patient label>.
  const storyMatch = segments.length === 3 && segments[0] === "results" && segments[1] === "story"
    ? (labels.stories as Record<string, string>)[segments[2]!]
    : undefined;
  if (storyMatch) segments = ["results", segments[2]];

  return [
    breadcrumbItem("Home", "/"),
    ...segments.map((seg, idx) => {
      const isStoryLeaf = Boolean(storyMatch) && idx === segments.length - 1;
      const path = isStoryLeaf
        ? `/results/story/${seg}`
        : `/${segments.slice(0, idx + 1).join("/")}`;
      const name = isStoryLeaf ? storyMatch! : clean === "/services/revisions/tore" && seg === "revisions" ? "Revisions" : labelFor(seg);
      return breadcrumbItem(name, path);
    }),
  ];
}

export function buildBreadcrumbSchema(pathname: string): Record<string, unknown> | null {
  const clean = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  const items = buildBreadcrumbItems(clean);
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}${clean}#breadcrumb`,
    itemListElement: items.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      // Google's documented BreadcrumbList shape accepts the canonical URL
      // directly. This avoids third-party crawlers treating nested WebPage
      // objects as separate, incomplete breadcrumb records.
      item: crumb.url,
    })),
  };
}

